// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  InputCheckbox,
  ListDescription,
  LoadingEmptyState,
  MdSpacing,
  SmSpacing,
  XlPadding,
} from '@alto/design-system';
import React, { useState } from 'react';
import { getCompanyName } from '~shared/config/getCompanyName';
import { type SubscriptionType, subscriptionsConstants } from '~shared/constants';
import { useUpdateUserSubscriptions, useUserSubscriptions } from '~shared/queries/query-keys/subscriptions';
import { StayInTouch } from './StayInTouch';

type Props = {
  readonly setIsUpdated: (isUpdated: boolean) => void;
  readonly uuid: string;
};

export const SubscriptionOptions = ({ setIsUpdated, uuid }: Props) => {
  const { email, subscriptions: fetchedSubscriptions, error: fetchError, isPending } = useUserSubscriptions({ uuid });
  const {
    mutateAsync: updateUserSubscriptions,
    error: updateError,
    isPending: isUpdating,
  } = useUpdateUserSubscriptions();

  // Checkbox selections the patient has changed since the fetch, keyed by comms_type.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const subscriptions = fetchedSubscriptions.map((subscription) => ({
    ...subscription,
    subscribed: overrides[subscription.comms_type] ?? subscription.subscribed,
  }));
  const isChanged = Object.keys(overrides).length > 0;
  const errorMessage = fetchError?.message || updateError?.message;

  const toggleSubscription = (comms_type: string) => {
    const current = subscriptions.find((subscription) => subscription.comms_type === comms_type)?.subscribed ?? false;
    setOverrides((prev) => ({ ...prev, [comms_type]: !current }));
  };

  const handleUpdate = async (unSubscribeAll = false) => {
    try {
      await updateUserSubscriptions({ uuid, subscriptions, unSubscribeAll });
      setIsUpdated(true);
    } catch {
      // mutateAsync rejects on failure; the form stays mounted and updateError renders above.
    }
  };

  if (isPending || isUpdating) {
    return <LoadingEmptyState />;
  }

  return (
    <>
      {errorMessage ? <Body center>{errorMessage}</Body> : null}
      <StayInTouch email={email} />
      <XlPadding topPadding={SPACING.STATIC.NONE}>
        <Column gap="XS">
          {subscriptions.map(({ comms_type, subscribed }) => (
            <InputCheckbox
              noHighlight
              checkPlacement="left"
              name={comms_type}
              label={subscriptionsConstants[comms_type as SubscriptionType].category}
              descriptions={
                <ListDescription>{subscriptionsConstants[comms_type as SubscriptionType].description}</ListDescription>
              }
              key={comms_type}
              value={subscribed}
              onPress={() => {
                toggleSubscription(comms_type);
              }}
            />
          ))}
        </Column>
      </XlPadding>
      <Button
        disabled={!isChanged}
        label="Update"
        type="primary"
        onPress={() => handleUpdate()}
      />
      <SmSpacing />
      <Button
        label="Or unsubscribe all"
        onPress={() => {
          handleUpdate(true);
        }}
        type="tertiary"
      />
      <MdSpacing />
      <Body
        center
        fontFamily="semibold"
        textSize="detail"
        color={COLORS.PALETTE.GREYSCALE.DARKER}
      >
        <strong>Please note:</strong> you may still receive account-related emails from ${getCompanyName()} related to
        the management and delivery of your medications.
      </Body>
    </>
  );
};
